import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Copy, ExternalLink, Loader2, Mail } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import ReopenNameDialog from "@/components/admin/ReopenNameDialog";
import SendCustomEmailDialog from "@/components/admin/SendCustomEmailDialog";
import AffiliationAgreementCard from "@/components/admin/AffiliationAgreementCard";
import MemberJourneyCard from "@/components/admin/MemberJourneyCard";
import { GateStrip, gatesFor } from "@/components/admin/GateStrip";
import { InviteToPortalButton, SendResetButton } from "@/components/admin/UserAccessActions";
import { isOnboardingComplete, isSuitePath } from "@/lib/onboardingComplete";
import { fieldLabel, fieldValueText } from "@/lib/memberFieldDisplay";
import { toAddress } from "@/components/onboarding/AddressFields";

import {
  addressLines,
  hasSeparateShipping,
  legalAddressOf,
  shippingAddressOf,
  shippingDestination,
  unaAddressDisplay,
} from "@/lib/shippingDestination";

const STAGES = ["ready", "ein_acquired", "filings_submitted", "filings_received", "docs_sent", "docs_delivered"];
const STAGE_LABEL: Record<string, string> = {
  pending: "Not started",
  ready: "Ready",
  ein_acquired: "EIN in hand",
  filings_submitted: "Filed with the state",
  filings_received: "State has processed",
  docs_sent: "Documents shipped",
  docs_delivered: "Documents delivered",
};

const SEGMENT_LABEL: Record<string, string> = {
  seg_solo: "Handles it all themselves",
  seg_seasonal: "Keeps it simple, sees a tax professional once a year",
  seg_counseled: "Has an entity or two and standing professional relationships",
  seg_house: "Established planning in place",
};

const HOME_LABEL: Record<string, string> = {
  solo: "Lives alone",
  us: "Married couple",
  us_kids: "Married with children at home",
  me_kids: "Single parent with children at home",
  us_kids_grown: "Married with grown children",
  me_kids_grown: "Single with grown children",
};

const CA_OPTION_LABEL: Record<string, string> = {
  own: "Uses an address they already own",
  self_sourced: "Found their own California address",
  platform: "Uses our California address",
};

/** A quiet section heading inside the details grid. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="sm:col-span-2 lg:col-span-3 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/** One quiet row: muted label above the value. Empty values never render. */
function Row({
  label,
  value,
  children,
}: {
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const body = children ?? value;
  if (body === null || body === undefined || body === "" || body === false) return null;
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">{body}</div>
    </div>
  );
}

/** Long audit values stay on one line until you click them. */
function LongValue({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-muted-foreground">Empty</span>;
  if (text.length <= 60) return <span>{text}</span>;
  return (
    <button
      type="button"
      title={text}
      onClick={() => setOpen((v) => !v)}
      className={open ? "text-left break-words" : "text-left truncate max-w-[16rem] block"}
    >
      {open ? text : `${text.slice(0, 57)}...`}
    </button>
  );
}

export default function AdminMemberRecord() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [names, setNames] = useState<any[]>([]);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState("");
  const [customEmailOpen, setCustomEmailOpen] = useState(false);
  const [suiteNumber, setSuiteNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<any>(null);
  const [adminNote, setAdminNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [affiliationUpload, setAffiliationUpload] = useState<{ file_path: string; created_at: string } | null>(null);
  const [recordNotes, setRecordNotes] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data: rec, error } = await (supabase as any)
      .from("member_records")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) toast.error(error.message);
    setRecord(rec);

    const [{ data: hist }, { data: evs }, { data: nm }, { data: recNotes }] = await Promise.all([
      (supabase as any).from("member_record_history").select("*").eq("record_id", id).order("changed_at", { ascending: false }).limit(200),
      (supabase as any).from("fulfillment_events").select("*").eq("member_record_id", id).order("created_at", { ascending: false }).limit(100),
      (supabase as any).from("name_review_events").select("*").eq("member_record_id", id).order("submitted_at", { ascending: false }).limit(50),
      (supabase as any)
        .from("admin_member_notes")
        .select("id, body, created_at, author_user_id")
        .eq("member_record_id", id)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    setHistory(hist || []);
    setEvents(evs || []);
    setNames(nm || []);
    setRecordNotes(recNotes || []);

    if (rec?.user_id) {
      const { data: m } = await (supabase as any).rpc("admin_member_account_meta", {
        _user_id: rec.user_id,
      });
      setMeta(m || null);

      const { data: aff } = await (supabase as any)
        .from("user_journey_documents")
        .select("file_path, created_at")
        .eq("user_id", rec.user_id)
        .eq("document_type", "affiliation_agreement")
        .order("created_at", { ascending: false })
        .limit(1);
      setAffiliationUpload(aff?.[0] || null);
    } else {
      setMeta(null);
      setAffiliationUpload(null);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveAdminNote = async () => {
    if (!adminNote.trim() || !record?.id) return;
    setSavingNote(true);
    const { data: userRes } = await supabase.auth.getUser();
    const author = userRes?.user?.id;
    if (!author) {
      setSavingNote(false);
      toast.error("You are not signed in");
      return;
    }
    const { error } = await (supabase as any).from("admin_member_notes").insert({
      member_record_id: record.id,
      member_user_id: record.user_id ?? null,
      author_user_id: author,
      body: adminNote.trim(),
    });
    setSavingNote(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setAdminNote("");
    toast.success("Note saved");
    void load();
  };

  /** Storage-backed instruments open through a short lived signed link. */
  const openStoredDoc = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("journey-documents")
      .createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      toast.error("That file would not open. Try again in a moment.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const copyEmail = () => {
    const email = meta?.email || record?.email;
    if (!email) return;
    void navigator.clipboard.writeText(email);
    toast.success("Email copied");
  };

  const nextStage = () => {
    if (!record) return null;
    if (record.fulfillment_stage === "pending") return "ready";
    const i = STAGES.indexOf(record.fulfillment_stage);
    return i >= 0 && i < STAGES.length - 1 ? STAGES[i + 1] : null;
  };

  const advance = async () => {
    const to = nextStage();
    if (!record || !to) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("fulfillment-advance", {
      body: { member_record_id: record.id, to_stage: to, note, tracking_number: tracking },
    });
    setBusy(false);
    const failure = error?.message || (data as any)?.error;
    if (failure) {
      toast.error(String(failure));
      return;
    }
    toast.success(`Moved to ${STAGE_LABEL[to]}`);
    setAdvanceOpen(false);
    setNote("");
    void load();
  };

  const assignSuite = async () => {
    if (!record) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("reserve-suite", {
      body: {
        member_record_id: record.id,
        number: suiteNumber ? Number(suiteNumber) : undefined,
      },
    });
    setBusy(false);
    const failure = error?.message || (data as any)?.error;
    if (failure) {
      toast.error(String(failure));
      return;
    }
    toast.success("Suite assigned");
    setSuiteNumber("");
    void load();
  };

  const gates = useMemo(() => (record ? gatesFor(record) : []), [record]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }

  if (!record) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 text-muted-foreground">
        No record found.
      </div>
    );
  }

  const fullName = [record.first_name, record.last_name].filter(Boolean).join(" ") || record.email;
  const latestNameEvent = names[0];
  const latestReopen =
    latestNameEvent && latestNameEvent.event_type === "reopened" ? latestNameEvent : null;
  const email: string = meta?.email || record.email || "";
  const roles: string[] = Array.isArray(meta?.roles) ? meta.roles : [];
  const roleLabel = roles.includes("admin") ? "Admin" : "Member";
  const joinedAt = meta?.joined_at ? new Date(meta.joined_at) : null;
  const lastSignIn = meta?.last_sign_in_at ? new Date(meta.last_sign_in_at) : null;
  const showPortalCard = record.fulfillment_stage === "docs_delivered";
  const isDormant = record.record_status === "dormant";
  // Their Association is already with us: documents delivered, or they came over
  // in the legacy import. Those members get a way in, not an onboarding. A
  // dormant person gets neither until they come back through onboarding.
  const portalEligible =
    !isDormant &&
    (record.fulfillment_stage === "docs_delivered" || record.origin === "legacy_import");
  // Notes can hang on the person or on the record, so we show both, newest first.
  const metaNotes: any[] = Array.isArray(meta?.notes) ? meta.notes : [];
  const notes: any[] = [
    ...metaNotes,
    ...recordNotes.filter((n) => !metaNotes.some((m: any) => m.id === n.id)),
  ].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const inFormation = isOnboardingComplete(record as never);
  const currentGate = gates.find((g) => g.state !== "passed") ?? null;
  const shipsTo = shippingDestination(record).address;
  const shipCity = [shipsTo.city, shipsTo.state].filter(Boolean).join(", ");
  const suitePath = isSuitePath(record as never);
  const affiliationPath: string | null =
    record.affiliation_document_path || affiliationUpload?.file_path || null;
  const affiliationLabel = record.affiliation_document_path
    ? `Affiliation Agreement${record.affiliation_integrated_at ? ` (filed ${new Date(record.affiliation_integrated_at).toLocaleDateString()})` : ""}`
    : "Affiliation Agreement (waiting on us)";

  const signedDocs: {
    label: string;
    url?: string | null;
    path?: string | null;
    signedAt?: string | null;
  }[] = [
    { label: "MEA PDF", url: record.mea_document_url, signedAt: record.mea_signed_at },
    { label: "Lease PDF", url: record.lease_document_url, signedAt: record.lease_signed_at },
    { label: "NDA PDF", url: record.nda_document_url, signedAt: record.nda_signed_at },
    { label: affiliationLabel, path: affiliationPath },
  ].filter((d) => Boolean(d.url || d.path));

  const legal = addressLines(legalAddressOf(record));
  const mailing = addressLines(toAddress(record.mailing_address));
  const shipping = hasSeparateShipping(record) ? addressLines(shippingAddressOf(record)) : [];
  const destination = addressLines(shipsTo);
  const una = unaAddressDisplay(record);


  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <Link to="/admin/fulfillment" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Fulfillment
          </Link>
          <h1 className="text-2xl font-semibold">{fullName}</h1>
          <p className="text-sm text-muted-foreground">{record.una_name || "No association name yet"}</p>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="break-all">{email || "No email on file"}</span>
            {email && (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={copyEmail}
                title="Copy email"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            )}
            <Badge variant="outline">{roleLabel}</Badge>
            {isDormant && <Badge variant="secondary">Dormant</Badge>}
          </div>
          <div className="text-xs text-muted-foreground">
            Joined: {joinedAt ? format(joinedAt, "PP") : "Not known"}
            <span className="mx-2">|</span>
            Last sign in: {lastSignIn ? formatDistanceToNow(lastSignIn, { addSuffix: true }) : "Never"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {email && (
            <Button size="sm" variant="outline" onClick={() => setCustomEmailOpen(true)}>
              <Mail className="h-4 w-4 mr-2" />
              Send email
            </Button>
          )}
          {email &&
            (record.user_id ? (
              <SendResetButton email={email} />
            ) : portalEligible ? (
              <InviteToPortalButton memberRecordId={record.id} onSent={() => void load()} />
            ) : null)}
          <Badge variant="secondary">{STAGE_LABEL[record.fulfillment_stage] || record.fulfillment_stage}</Badge>
          {nextStage() && (
            <Button size="sm" onClick={() => setAdvanceOpen(true)}>
              Move to {STAGE_LABEL[nextStage() as string]}
            </Button>
          )}
        </div>
      </div>

      {/* Where they stand: one quiet line. */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          {inFormation ? (
            <div className="text-sm">
              <span className="text-muted-foreground">Formation: </span>
              {STAGE_LABEL[record.fulfillment_stage] || record.fulfillment_stage}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-4">
              <GateStrip record={record} gates={gates} showPaid={false} />
              <span className="text-sm text-muted-foreground">
                {currentGate ? currentGate.waiting : "Ready for handoff"}
              </span>
            </div>
          )}
          {record.suite_number != null && (
            <span className="text-sm text-muted-foreground">Suite {record.suite_number}</span>
          )}
          {suitePath && (
            <span className="text-sm text-muted-foreground">
              {record.payment_status === "complete" ? "Paid monthly fee" : "Monthly fee not started"}
            </span>
          )}
          {shipCity && <span className="text-sm text-muted-foreground">Ships to {shipCity}</span>}
        </CardContent>
      </Card>

      <MemberJourneyCard record={record} names={names} events={events} />

      {showPortalCard && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Portal</CardTitle>
            <CardDescription>How they are doing inside the portal.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
            <Link to="/admin/system/governance-health" className="text-sm hover:underline">
              <div className="text-xs text-muted-foreground">Governance health</div>
              <div className="text-xl font-semibold">{meta?.health_score ?? "n/a"}</div>
            </Link>
            <Link to="/admin/gathering-questions" className="text-sm hover:underline">
              <div className="text-xs text-muted-foreground">Open questions</div>
              <div className="text-xl font-semibold">{meta?.open_questions ?? 0}</div>
            </Link>
            <Link
              to={`/admin/users/tasks?userId=${record.user_id || ""}`}
              className="text-sm hover:underline"
            >
              <div className="text-xs text-muted-foreground">Open tasks</div>
              <div className="text-xl font-semibold">{meta?.open_tasks ?? 0}</div>
            </Link>
            <Link
              to={`/admin/prospect-conversations?userId=${record.user_id || ""}`}
              className="text-sm hover:underline"
            >
              <div className="text-xs text-muted-foreground">Ask Stew, last 30 days</div>
              <div className="text-xl font-semibold">{meta?.stew_sessions_recent ?? 0}</div>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Details: everything worth knowing at a glance, empty rows dropped. */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Details</CardTitle>
            <CardDescription>Only what we actually have on file.</CardDescription>
          </div>
          {(record.una_name_confirmed_at || record.una_name_status === "approved") && (
            <ReopenNameDialog memberRecordId={record.id} memberName={fullName} onDone={load} />
          )}
        </CardHeader>
        <CardContent className="grid gap-x-8 gap-y-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <SectionLabel>Person</SectionLabel>
          {record.legal_full_name && (
            <Row label="Treasurer legal name" value={record.legal_full_name} />
          )}
          <Row label="Phone" value={record.phone || ""} />
          <Row label="Spouse" value={record.spouse_name || ""} />
          <Row label="Household" value={HOME_LABEL[record.home_situation as string] || ""} />
          {record.adult_children_count !== null && record.adult_children_count !== undefined && (
            <Row label="Adult children" value={String(record.adult_children_count)} />
          )}

          {(record.principal_officer_is_self === true ||
            record.principal_officer_name ||
            record.principal_officer_email) && (
            <>
              <SectionLabel>Principal officer</SectionLabel>
              {record.principal_officer_is_self === true ? (
                <Row label="Principal Officer" value="The member themself" />
              ) : (
                <>
                  <Row label="Principal Officer name" value={record.principal_officer_name || ""} />
                  <Row label="Principal Officer email" value={record.principal_officer_email || ""} />
                </>
              )}
            </>
          )}

          <SectionLabel>Association</SectionLabel>
          <Row label="Association name">
            <div>
              <div>{record.una_name || "Not chosen yet"}</div>
              {record.una_name_confirmed_at ? (
                <div className="text-xs text-muted-foreground">
                  Confirmed {new Date(record.una_name_confirmed_at).toLocaleDateString()}
                </div>
              ) : record.una_name_status === "approved" ? (
                <div className="text-xs text-amber-600 dark:text-amber-500">
                  Awaiting member confirmation
                </div>
              ) : null}
            </div>
          </Row>
          <Row label="UNA address">
            {una.lines.length > 0 ? (
              <div>{una.lines.map((line) => <div key={line}>{line}</div>)}</div>
            ) : (
              <span className="text-muted-foreground">{una.note}</span>
            )}
          </Row>
          <Row
            label="California address choice"
            value={CA_OPTION_LABEL[record.ca_address_option as string] || ""}
          />
          {record.ein ? (
            <Row label="EIN">
              <span className="font-mono">{record.ein}</span>
            </Row>
          ) : null}
          <Row
            label="Finance answers"
            value={SEGMENT_LABEL[record.finance_segment as string] || ""}
          />
          <Row label="Association amount" value={record.una_amount || ""} />
          <Row label="Monthly amount" value={record.monthly_amount || ""} />
          {record.chancery_interest === true && (
            <Row
              label="Open to an administration conversation"
              value={`Yes${record.chancery_interest_at ? ` on ${new Date(record.chancery_interest_at).toLocaleDateString()}` : ""}`}
            />
          )}

          <SectionLabel>Addresses</SectionLabel>
          {legal.length > 0 && (
            <Row label="Legal address">
              <div>{legal.map((line) => <div key={line}>{line}</div>)}</div>
            </Row>
          )}
          {mailing.length > 0 && (
            <Row label="Mailing address">
              <div>{mailing.map((line) => <div key={line}>{line}</div>)}</div>
            </Row>
          )}
          {shipping.length > 0 && (
            <Row label="Shipping address">
              <div>{shipping.map((line) => <div key={line}>{line}</div>)}</div>
            </Row>
          )}
          {destination.length > 0 && (
            <Row label="Documents ship to">
              <div>{destination.map((line) => <div key={line}>{line}</div>)}</div>
            </Row>
          )}

          {signedDocs.length > 0 && (
            <>
              <SectionLabel>Documents</SectionLabel>
              <Row label="Signed documents">
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {signedDocs.map((d) => (
                    <div key={d.label}>
                      {d.url ? (
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 underline underline-offset-2"
                        >
                          {d.label}
                          <ExternalLink className="h-3 w-3" aria-hidden />
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void openStoredDoc(d.path as string)}
                          className="inline-flex items-center gap-1 underline underline-offset-2"
                        >
                          {d.label}
                          <ExternalLink className="h-3 w-3" aria-hidden />
                        </button>
                      )}
                      {d.signedAt ? (
                        <div className="text-xs text-muted-foreground">
                          Signed {new Date(d.signedAt).toLocaleDateString()}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </Row>
            </>
          )}


          {latestReopen && record.una_name_status !== "approved" && (
            <div className="sm:col-span-2 lg:col-span-3 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              <div className="font-medium">
                Name reopened on{" "}
                {new Date(latestReopen.decided_at || latestReopen.submitted_at).toLocaleDateString()}
              </div>
              <p className="mt-1">{latestReopen.note || latestReopen.payload?.reason || ""}</p>
            </div>
          )}

          {(!record.una_amount || !record.monthly_amount) && !record.mea_signed_at && (
            <p className="sm:col-span-2 lg:col-span-3 text-sm text-amber-600 dark:text-amber-500">
              No amounts on file, MEA will render blank
            </p>
          )}
        </CardContent>
      </Card>

      {/* Everything else is one click deep. */}
      <Card>
        <CardContent className="p-4">
          <Accordion
            type="multiple"
            className="w-full"
            defaultValue={showPortalCard ? ["affiliation"] : []}
          >
            <AccordionItem value="suite">
              <AccordionTrigger className="py-3 text-base">
                Suite {record.suite_number != null ? `(${record.suite_number})` : ""}
              </AccordionTrigger>
              <AccordionContent>
                <div className="mb-3 text-sm text-muted-foreground">
                  Current suite: {record.suite_number != null ? record.suite_number : "none"}
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="suite">Explicit number (optional)</Label>
                    <Input
                      id="suite"
                      className="w-40"
                      value={suiteNumber}
                      onChange={(e) => setSuiteNumber(e.target.value)}
                    />
                  </div>
                  <Button variant="outline" onClick={() => void assignSuite()} disabled={busy}>
                    Assign suite
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="events">
              <AccordionTrigger className="py-3 text-base">
                Fulfillment events {events.length ? `(${events.length})` : ""}
              </AccordionTrigger>
              <AccordionContent>
                {events.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>From</TableHead>
                          <TableHead>To</TableHead>
                          <TableHead>Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {events.map((e) => (
                          <TableRow key={e.id}>
                            <TableCell className="text-xs">{new Date(e.created_at).toLocaleString()}</TableCell>
                            <TableCell className="text-xs">{STAGE_LABEL[e.from_stage] || e.from_stage}</TableCell>
                            <TableCell className="text-xs">{STAGE_LABEL[e.to_stage] || e.to_stage}</TableCell>
                            <TableCell className="text-xs">{e.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="names">
              <AccordionTrigger className="py-3 text-base">
                Name history {names.length ? `(${names.length})` : ""}
              </AccordionTrigger>
              <AccordionContent>
                {names.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Decision</TableHead>
                          <TableHead>Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {names.map((n) => (
                          <TableRow key={n.id}>
                            <TableCell className="text-xs">{new Date(n.submitted_at).toLocaleString()}</TableCell>
                            <TableCell className="text-xs">{n.submitted_name}</TableCell>
                            <TableCell className="text-xs">{n.decision || "waiting"}</TableCell>
                            <TableCell className="text-xs">{n.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="history">
              <AccordionTrigger className="py-3 text-base">
                Field history {history.length ? `(${history.length})` : ""}
              </AccordionTrigger>
              <AccordionContent>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Field</TableHead>
                          <TableHead>Was</TableHead>
                          <TableHead>Now</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell className="text-xs whitespace-nowrap">
                              {new Date(h.changed_at).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-xs">{fieldLabel(h.field_key)}</TableCell>
                            <TableCell className="text-xs">
                              <LongValue text={fieldValueText(h.old_value)} />
                            </TableCell>
                            <TableCell className="text-xs">
                              <LongValue text={fieldValueText(h.new_value)} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {record.user_id && (
              <AffiliationAgreementCard
                userId={record.user_id}
                memberName={fullName}
                memberRecordId={record.id}
                onIntegrated={() => void load()}
              />
            )}

            <AccordionItem value="notes" className="border-b-0">
              <AccordionTrigger className="py-3 text-base">
                Admin notes (internal) {notes.length ? `(${notes.length})` : ""}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Only admins can see these notes. Members never see them.
                  </p>
                  <Textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add a note about this member"
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => void saveAdminNote()}
                      disabled={!adminNote.trim() || savingNote}
                    >
                      {savingNote ? "Saving..." : "Save note"}
                    </Button>
                  </div>
                  <Separator className="my-2" />
                  {notes.length ? (
                    <ul className="space-y-3 max-h-72 overflow-auto">
                      {notes.map((n: any) => (
                        <li key={n.id} className="text-sm">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{n.author_name || "Admin"}</span>
                            <span>
                              {n.created_at
                                ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true })
                                : ""}
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap">{n.body}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No notes yet.</p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to {STAGE_LABEL[nextStage() || ""]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {nextStage() === "docs_sent" && (
              <div className="space-y-2">
                <Label htmlFor="tracking">Tracking number</Label>
                <Input id="tracking" value={tracking} onChange={(e) => setTracking(e.target.value)} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceOpen(false)}>Cancel</Button>
            <Button onClick={() => void advance()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save the move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendCustomEmailDialog
        open={customEmailOpen}
        onOpenChange={setCustomEmailOpen}
        record={{
          id: record.id,
          email,
          first_name: record.first_name,
          una_name: record.una_name,
          tracking_number: record.tracking_number,
        }}
      />
    </div>
  );
}
